"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CORE_COLORS } from "@/lib/theme/core";

/**
 * Edge affordances for a horizontally scrollable row.
 *
 * QA: "for any horizontal scrollable content, add < and > arrow signs to
 * indicate that the content is scrollable, do not show < or > if the direction
 * is not scrollable." So each arrow is bound to ITS OWN direction: the left one
 * only exists once you have scrolled away from the start, the right one only
 * while there is more to reach. A row that fits shows neither.
 *
 * `useScrollEdges` is the measurement half — a scroll + resize listener plus a
 * `MutationObserver`, because several of these rows change their contents
 * (the 信報專題 tab strip swaps panels, the section chip rows are fed by
 * fixtures) and a row that grows after mount must re-evaluate.
 */
export function useScrollEdges<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
) {
  const [edges, setEdges] = React.useState({ start: false, end: false });

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      // 1px of slack: fractional layout widths otherwise leave `end` true on a
      // row that is already scrolled fully to its end.
      const max = el.scrollWidth - el.clientWidth;
      setEdges({ start: el.scrollLeft > 1, end: el.scrollLeft < max - 1 });
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    for (const child of el.children) ro.observe(child);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [ref]);

  return edges;
}

/**
 * The arrows themselves, positioned over a `relative` wrapper around the
 * scroller.
 *
 * They scroll rather than merely indicate — an arrow that looks like a control
 * and does nothing is the defect QA has filed repeatedly. `aria-hidden` is
 * wrong here for the same reason, so they are real buttons with labels; the row
 * is still swipeable and keyboard-scrollable without them.
 */
export function ScrollEdgeArrows({
  scroller,
  edges,
  /** How far one press moves the row; defaults to ~80% of what is on screen. */
  step,
  className = "",
  /**
   * Chevron colour. Defaults to the main-site green; a vertical passes its own
   * accent so the arrows do not paint HKEJ green inside another brand.
   */
  arrowColor = CORE_COLORS.brandGreen,
  /** Hover tint behind the chevron. Same reason as `arrowColor`. */
  hoverBg,
}: {
  scroller: React.RefObject<HTMLElement | null>;
  edges: { start: boolean; end: boolean };
  step?: number;
  className?: string;
  arrowColor?: string;
  hoverBg?: string;
}) {
  const go = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    const by = step ?? Math.max(120, el.clientWidth * 0.8);
    el.scrollBy({ left: dir * by, behavior: "smooth" });
  };

  return (
    <>
      {edges.start && (
        <ScrollArrow
          side="start"
          onClick={() => go(-1)}
          className={className}
          arrowColor={arrowColor}
          hoverBg={hoverBg}
        />
      )}
      {edges.end && (
        <ScrollArrow
          side="end"
          onClick={() => go(1)}
          className={className}
          arrowColor={arrowColor}
          hoverBg={hoverBg}
        />
      )}
    </>
  );
}

function ScrollArrow({
  side,
  onClick,
  className,
  arrowColor,
  hoverBg,
}: {
  side: "start" | "end";
  onClick: () => void;
  className?: string;
  arrowColor: string;
  hoverBg?: string;
}) {
  const Icon = side === "start" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={side === "start" ? "向左捲動" : "向右捲動"}
      onClick={onClick}
      // Square, borderless, no shadow. The hover moves to the BACKGROUND
      // because there is no longer a border to recolour — the control still has
      // to answer the pointer, which is the affordance QA has asked for
      // throughout; `#f5fbf8` is the same tint every 關注 button uses.
      //
      // The resting fill goes through a custom property rather than an inline
      // `background-color`, for the usual reason: an inline declaration would
      // outrank the `hover:bg-*` class and the hover would never apply.
      className={`absolute top-1/2 z-10 inline-flex size-[28px] -translate-y-1/2 items-center justify-center rounded-none border-0 bg-[color:var(--rest-bg)] transition-colors hover:bg-[color:var(--hover-bg)] ${
        side === "start" ? "left-0" : "right-0"
      } ${className}`}
      style={
        {
          boxShadow: "none",
          ["--rest-bg" as string]: "rgba(255,255,255,0.95)",
          ["--hover-bg" as string]: hoverBg ?? "#f5fbf8",
        } as React.CSSProperties
      }
    >
      <Icon className="size-[16px]" strokeWidth={2} color={arrowColor} />
    </button>
  );
}
